import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';

import { Button } from '$partials/ui';
import { Header, Footer } from '$partials/global/index.js';
import { Meta } from '$partials/global';
import GlossaryModal from '$partials/glossary/glossary-modal.jsx';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof glossaryListItemPropTypes>} props
 */
const GlossaryListItem = ({
    borderTop = false,
    index = 0,
    id,
    term,
    totalItems = 0
}) => {
    const [modalOpen, setModalOpen] = useState(false);

    return (
        <>
            <li
                className={`w-full flex items-start ${borderTop ? 'border-t border-t-charcoal' : ''}`}
                id={`term-${id}`}>
                <p className='type-style-4 pr-30 text-left py-30 flex items-end'>
                    {String(index).padStart(Math.max(String(totalItems - 1).length, 2), '0')}
                </p>

                <Button
                    className='block type-style-3 py-30 text-left'
                    onClick={() => setModalOpen(true)}>
                    {term}
                </Button>
            </li>

            {(modalOpen) &&
                <GlossaryModal
                    isOpen={true}
                    onClose={() => setModalOpen(false)}
                    termId={id} />
            }
        </>
    );
};

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const GlossaryPage = (props) => {
    // references
    const watchedElemRef = useRef();

    const page = props.data.page;

    const letterArray = page?.glossaryItems.map((item) => item.term.slice(0, 1));
    const firstLetterArray = [...new Set(letterArray)];

    // state
    const [ headerHeight, setHeaderHeight ] = useState();

    return (
        <>
            <Meta
                description={page.searchDescription || page.listingText}
                domain={props.domain}
                image={page.listingImage}
                page={page} />

            <Header
                {...props?.data?.navigation}
                {...props?.data?.ticker}
                headerHeight={(height) => setHeaderHeight(height)}
                pageType={page?.__typename}
                standfirst={page?.standfirst}
                watchedElemRef={watchedElemRef} />

            <main
                id='main-content'
                style={{marginTop: `${headerHeight}px`}}>
                <section
                    className='layout-container pt-30 sm:pt-20 md:pt-0 mb-60 md:mb-100 title-height'
                    style={{top: `${headerHeight}px`}}>
                    <div className='layout-grid-negative-gutter md:flex'>
                        <div className='md:w-1/12 layout-grid-col-padding' />

                        <div className='md:w-7/12 layout-grid-col-padding'>
                            {page?.title && (
                                <h1 className='type-style-4 pb-30'>{page.title}</h1>
                            )}

                            {page?.heading && (
                                <p className='type-style-1'>{page.heading}</p>
                            )}
                        </div>
                    </div>
                </section>

                <div
                    className='border-t border-t-charcoal'
                    ref={watchedElemRef} />

                <section className='relative layout-container bg-white'>
                    <div className='layout-grid-negative-gutter flex'>
                        <div className='hidden md:block w-1/12 layout-grid-col-padding' />

                        <ul className='w-full md:w-11/12'>
                            {firstLetterArray.map((letter, i) => {
                                let itemIndex = 0;

                                return (
                                    <li key={i}>
                                        <div className='layout-grid-col-padding'>
                                            <div className={`${i === 0 ? '' : 'border-t border-t-charcoal'}`} />
                                        </div>

                                        <div className='flex glossary-term-row'>
                                            <p className='type-style-4 pr-30 w-2/12 sm:w-4/12 md:w-4/11 layout-grid-col-padding py-30'>
                                                {letter}
                                            </p>

                                            <ul className='layout-grid-col-padding w-10/12 sm:w-8/12 md:w-7/11'>
                                                {page?.glossaryItems.map((item, i) => {
                                                    if (item?.term?.slice(0, 1) === letter) {
                                                        const borderTop = itemIndex > 0;
                                                        itemIndex++;
                                                        return (
                                                            <GlossaryListItem
                                                                borderTop={borderTop}
                                                                id={item.id}
                                                                index={i}
                                                                key={item.id}
                                                                term={item.term}
                                                                totalItems={page?.glossaryItems.length} />
                                                        );
                                                    }
                                                })}
                                            </ul>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </section>

            </main>

            <Footer
                {...props?.data?.footer}
                {...props?.data?.newsletter} />
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

const propTypes = {
    data: PropTypes.shape({
        page: PropTypes.object,
        footer: PropTypes.object,
        navigation: PropTypes.object,
        newsletter: PropTypes.object,
        ticker: PropTypes.object,
    }),
    domain: PropTypes.string,
};

GlossaryPage.propTypes = propTypes;

const glossaryListItemPropTypes = {
    borderTop: PropTypes.bool.isRequired,
    id: PropTypes.string.isRequired,
    index: PropTypes.number.isRequired,
    term: PropTypes.string.isRequired,
    totalItems: PropTypes.number.isRequired
};

GlossaryListItem.propTypes = glossaryListItemPropTypes;

export default GlossaryPage;
